// ✅ src/pages/VehicleV2/VehicleTyreSupplierDetailsPrint.jsx
// PRINT page for: VehicleTyreSupplierDetailsPrint.aspx (Crystal)
// - Reads querystring params (suppliername/tyreserialno/fromDate/toDate/tyreId)
// - Calls backend: GET /api/vehicleTyreSupplierDetailReport/print
// - Shows ONE table with totals + A4 print CSS + Back + Print buttons

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/supplierDetailReport";

/* ---------------- helpers ---------------- */
const toStr = (v) => String(v ?? "").trim();
const toNum = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};

const pad2 = (n) => String(n).padStart(2, "0");
const fmtDate = (isoOrDate) => {
  if (!isoOrDate) return "";
  // if already dd/MM/yyyy coming from SQL convert(103)
  if (typeof isoOrDate === "string" && isoOrDate.includes("/")) return isoOrDate;
  const d = new Date(isoOrDate);
  if (Number.isNaN(d.getTime())) return String(isoOrDate);
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const fmtMoney2 = (v) => {
  if (v === null || v === undefined || v === "") return "0.00";
  const n = Number(String(v).replace(/,/g, ""));
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const sumRate = (rows) =>
  (rows || []).reduce((s, r) => {
    const raw = r?.fld_TyreAmounts ?? r?.Rate ?? r?.rate ?? 0;
    const n = Number(String(raw).replace(/,/g, ""));
    return s + (Number.isFinite(n) ? n : 0);
  }, 0);

export default function VehicleTyreSupplierDetailsPrint() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();

  // 🔑 usually from localStorage in your project
  const finYearId = toNum(localStorage.getItem("finyearid") || localStorage.getItem("finYearId") || 0);
  const branchIdLS = toNum(localStorage.getItem("branchid") || 0);
  const branchTypeLS = toStr(localStorage.getItem("branchtype") || "HO");
  const loginUserIdLS = toNum(localStorage.getItem("loginuserid") || 0);

  // ✅ querystring params (support both print flows)
  const params = useMemo(() => {
    const tyreId = toStr(sp.get("tyreId"));
    return {
      tyreId: tyreId ? tyreId : "",

      // ASPX used Session["suppliername"] and Session["tyreserialno"] with "%" fallback
      suppliername: toStr(sp.get("suppliername") || "%"),
      tyreserialno: toStr(sp.get("tyreserialno") || "%"),

      fromDate: toStr(sp.get("fromDate") || sp.get("purfromdate") || ""),
      toDate: toStr(sp.get("toDate") || sp.get("purtodate") || ""),

      // allow passing branch meta, else localStorage
      branchId: toNum(sp.get("branchId") || branchIdLS),
      branchType: toStr(sp.get("branchType") || branchTypeLS || "HO"),
      loginUserId: toNum(sp.get("loginUserId") || loginUserIdLS),
    };
  }, [sp, branchIdLS, branchTypeLS, loginUserIdLS]);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [rows, setRows] = useState([]);
  const [header, setHeader] = useState({
    supplier: "",
    serial: "",
    from: "",
    to: "",
  });

  useEffect(() => {
    let alive = true;

    const load = async () => {
      try {
        setLoading(true);
        setErr("");

        const res = await axios.get(`${API_BASE}/print`, {
          params: {
            tyreId: params.tyreId || "",
            suppliername: params.suppliername,
            tyreserialno: params.tyreserialno,
            fromDate: params.fromDate,
            toDate: params.toDate,
            branchId: params.branchId,
            branchType: params.branchType,
            loginUserId: params.loginUserId,
          },
          headers: finYearId ? { "x-finyearid": String(finYearId) } : {},
        });

        const d = res?.data || {};
        if (!d?.success) throw new Error(d?.message || "Failed to load print data");

        if (!alive) return;
        setRows(Array.isArray(d.rows) ? d.rows : []);
        setHeader({
          supplier: toStr(d?.meta?.supplier ?? ""),
          serial: toStr(d?.meta?.serial ?? ""),
          from: toStr(d?.meta?.fromDate ?? params.fromDate),
          to: toStr(d?.meta?.toDate ?? params.toDate),
        });
      } catch (e) {
        if (!alive) return;
        setErr(e?.response?.data?.message || e.message || "Failed to load print data");
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();
    return () => {
      alive = false;
    };
  }, [params, finYearId]);

  const totalRate = useMemo(() => sumRate(rows), [rows]);

  return (
    <div className="w-full bg-white print:max-w-none print:mx-0">
      {/* Print CSS */}
      <style>{`
        @media print {
          body { margin: 0; padding: 8px; font-size: 9pt; }
          .no-print { display: none !important; }
          table { border-collapse: collapse; font-size: 8pt; }
          th, td { border: 1px solid #000 !important; padding: 3px !important; }
        }
        @page {
          size: A4 portrait;
          margin: 0.35in;
        }
      `}</style>

      <div className="p-2 print:p-1">
        {/* Actions */}
        <div className="no-print flex items-center justify-between mb-3">
          <button onClick={() => navigate(-1)} className="px-4 py-2 border rounded hover:bg-gray-50">
            ← Back
          </button>
          <button
            onClick={() => window.print()}
            className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded shadow"
          >
            🖨️ Print
          </button>
        </div>

        {/* Loading/Error */}
        {loading ? (
          <div className="no-print p-4 border rounded bg-gray-50 text-sm">Loading print data...</div>
        ) : err ? (
          <div className="no-print p-4 border rounded bg-red-50 text-sm text-red-700">{err}</div>
        ) : null}

        {/* Header */}
        <div className="border-2 border-black mb-2">
          <div className="flex items-center p-2">
            <div className="w-20 h-16 border border-black flex items-center justify-center mr-4 bg-gray-800 text-white font-bold">
              <div className="text-center">
                <div className="text-xs">NISSI</div>
                <div className="text-xs">INFOTECH</div>
              </div>
            </div>

            <div className="flex-1 text-center leading-tight">
              <h1 className="text-lg font-bold">Nissi Infotech</h1>
              <div className="text-sm font-semibold">Nissi CIVIL CONSTRUCTION</div>
              <div className="text-[10px] mt-1">
                3/42-12, Nagappa Gounder St, kavundampalayam, Coimbatore - Tamil Nadu.
                <br />
                Phone No : 9003744644 &nbsp;&nbsp; infracosoft-implementa
              </div>
              <div className="text-sm font-bold underline mt-2">VEHICLE TYRE SUPPLIER DETAIL REPORT</div>

              {/* Sub header line (supplier/serial) */}
              <div className="text-[11px] mt-2">
                Supplier: <b>{header.supplier || "-"}</b> &nbsp;&nbsp; | &nbsp;&nbsp; Serial:{" "}
                <b>{header.serial || "-"}</b>
              </div>
            </div>

            <div className="w-48 text-right text-[10px] leading-tight">
              <div>INFRA SCREEN : B-51/1</div>
              <div>
                From: <b>{header.from || "-"}</b>
              </div>
              <div>
                To: <b>{header.to || "-"}</b>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border-2 border-black text-xs">
            <thead>
              <tr className="bg-gray-200">
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 35 }}>
                  S.No
                </th>
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 140 }}>
                  Supplier Name
                </th>
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 140 }}>
                  Tyre Serial Number
                </th>
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 85 }}>
                  Purchase Date
                </th>
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 80 }}>
                  Rate
                </th>
              </tr>
            </thead>

            <tbody>
              {!rows?.length ? (
                <tr>
                  <td colSpan={5} className="border border-black px-2 py-2 text-center">
                    No records found.
                  </td>
                </tr>
              ) : (
                rows.map((r, i) => (
                  <tr key={`${r?.fld_NewTyre_ID ?? "id"}-${i}`}>
                    <td className="border border-black px-1 py-1 text-center">{i + 1}</td>
                    <td className="border border-black px-1 py-1">{toStr(r?.fld_Purchasefrom)}</td>
                    <td className="border border-black px-1 py-1">{toStr(r?.fld_Tyreserialno)}</td>
                    <td className="border border-black px-1 py-1 text-center">{fmtDate(r?.fld_Purchasedate)}</td>
                    <td className="border border-black px-1 py-1 text-right">{fmtMoney2(r?.fld_TyreAmounts)}</td>
                  </tr>
                ))
              )}
            </tbody>

            {rows?.length ? (
              <tfoot>
                <tr>
                  <td colSpan={4} className="border border-black px-2 py-1 text-right font-bold">
                    Total
                  </td>
                  <td className="border border-black px-2 py-1 text-right font-bold">
                    {fmtMoney2(totalRate)}
                  </td>
                </tr>
              </tfoot>
            ) : null}
          </table>
        </div>

        {/* Footer note */}
        <div className="mt-3 border-2 border-black p-1 text-xs text-center">
          Report Created by Infracosoft version 2.1. www.infracosoft.com
        </div>

        {/* Bottom Print Button */}
        <div className="mt-4 text-center no-print">
          <button
            onClick={() => window.print()}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded shadow-lg"
          >
            🖨️ Print Report
          </button>
        </div>
      </div>
    </div>
  );
}